import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { refreshStampedBadges } from '@theme/stamped-badges';

/**
 * Run work once the page has finished loading and the main thread is free.
 *
 * Used for speculative fetches that must not compete with anything the shopper
 * can currently see.
 *
 * @param {() => void} callback - The work to run.
 */
function whenIdle(callback) {
  const schedule = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(callback, { timeout: 2000 });
    } else {
      setTimeout(callback, 200);
    }
  };

  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
}

/**
 * Module-level lock so the swipe hint (see ProductCard#scheduleSwipeHint) can
 * only ever be claimed by one card per page view, even if more than one
 * `<product-card>` believes it is the first in its own grid — e.g. a
 * Nosto-rendered grid replacing the native one, or a filter re-render handing
 * a second grid instance to the DOM before the first is disconnected.
 * @type {boolean}
 */
let swipeHintClaimed = false;

/**
 * Whether a card is actually visible, not just present in the DOM. Both
 * assets/nosto-grid-skeleton.js (while deciding between the native and
 * Nosto-rendered grid) and Nosto's own rendering can leave a `<product-card>`
 * in the tree but hidden, and `querySelector` does not know the difference —
 * it would happily hand back a card nobody can see.
 * @param {Element} card
 * @returns {boolean}
 */
function isCardVisible(card) {
  return card instanceof HTMLElement && card.offsetParent !== null && getComputedStyle(card).visibility !== 'hidden';
}

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  /** @type {IntersectionObserver | null} */
  #stampedObserver = null;

  /** @type {IntersectionObserver | null} */
  #hoverImageObserver = null;

  /** @type {number | undefined} */
  #swipeHintTimeoutId;

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  #fetchProductPageHandler = () => {
    if (!this.refs.quickAdd?.cachedProductHtml) {
      this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
    }
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.addEventListener('pointerenter', this.#loadDeferredGalleryImages);
    this.#bindGalleryHover();
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Ensure third-party review badges/widgets render when a card is mounted
    refreshStampedBadges();

    // Also trigger when the badge actually enters the viewport
    this.#observeStampedBadge();

    // Do not preload card hover/slideshow images on mount.
    // This keeps initial collection/home requests focused on only the visible card image.
    // The warm-up below is the one exception, and it waits for both the page load
    // and an idle frame before it fetches anything.
    this.#observeHoverImageWarmup();

    this.#scheduleSwipeHint();
  }

  updatedCallback() {
    super.updatedCallback();
    refreshStampedBadges();
    // A morph can replace .card-gallery, leaving the hover listeners on a detached node.
    this.#bindGalleryHover();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener('pointerenter', this.#loadDeferredGalleryImages);
    this.#unbindGalleryHover();
    this.#hoverImageObserver?.disconnect();
    this.#hoverImageObserver = null;
    window.removeEventListener('load', this.#startSwipeHintTimer);
    document.removeEventListener('nosto:grid-skeleton:reveal', this.#startSwipeHintTimer);
    clearTimeout(this.#swipeHintTimeoutId);
  }

  /**
   * Nudges the gallery image halfway toward the next slide and back, 2 seconds
   * after the grid is actually visible, as a one-off hint that the card can be
   * swiped. Scoped to only the first visible card of the collection/search
   * grid — the "new" Nosto card design these pages use — so it never fires on
   * other Nosto placements or on every card in the grid.
   *
   * Every qualifying card arms its own 2-second timer; `#maybeShowSwipeHint`
   * (run when that timer fires, not before) is what actually decides whether
   * THIS card is the first visible one, and `swipeHintClaimed` makes sure only
   * one of them can win. Deciding that earlier — at connect time, or right when
   * the Nosto skeleton reveals — was a race: visibility/layout for the first
   * few cards was not yet settled, so the check could land on a later card
   * (observed: the 4th) instead of the true first. Two full seconds past
   * load/reveal is long enough for that to have resolved either way.
   */
  #scheduleSwipeHint() {
    // Swiping is the mobile interaction — desktop shows the next image on
    // hover already, so the hint would be teaching a gesture desktop never
    // uses it for.
    if (!isMobileBreakpoint()) return;

    const { classList } = document.body;
    const isListingPage = classList.contains('collection-page') || classList.contains('search-page');
    if (!isListingPage) return;

    // assets/nosto-grid-skeleton.js can hold the native grid hidden for several
    // seconds while it waits to see whether Nosto will render instead. Starting
    // the countdown from `load` in that case could still land inside that
    // window. Wait for the skeleton's own reveal signal first; only fall back
    // to `load` when nothing is gating this grid in the first place.
    if (document.documentElement.hasAttribute('data-nosto-grid-skeleton')) {
      document.addEventListener('nosto:grid-skeleton:reveal', this.#startSwipeHintTimer, { once: true });
    } else if (document.readyState === 'complete') {
      this.#startSwipeHintTimer();
    } else {
      window.addEventListener('load', this.#startSwipeHintTimer, { once: true });
    }
  }

  #startSwipeHintTimer = () => {
    this.#swipeHintTimeoutId = window.setTimeout(this.#maybeShowSwipeHint, 2000);
  };

  #maybeShowSwipeHint = () => {
    if (swipeHintClaimed || !this.isConnected || !isMobileBreakpoint()) return;

    const grid = this.closest('.product-grid');
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll('product-card'));
    if (cards.find(isCardVisible) !== this) return;

    swipeHintClaimed = true;
    this.refs.slideshow?.peekNext();
  };

  /**
   * Fetch the hover image before the pointer arrives.
   *
   * Cards whose gallery is turned off render their second image deferred (see
   * defer_media_load in snippets/card-gallery.liquid), and the CSS hover swap is
   * gated on it having loaded — so on `pointerenter` alone the first hover of
   * every card waits out a round trip and reads as laggy.
   *
   * The mount-time preload ban above still holds: this waits for the page load
   * event, then an idle frame, and fetches at low priority, so the warm-up never
   * competes with the card images the shopper is actually looking at.
   */
  #observeHoverImageWarmup() {
    if (!isDesktopBreakpoint()) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const gallery = this.refs.cardGallery ?? this.querySelector('.card-gallery');
    // Slideshow galleries ship every image in the markup; only the simple
    // gallery has anything deferred to warm up.
    if (!(gallery instanceof HTMLElement) || gallery.dataset.simpleGallery !== 'true') return;

    this.#hoverImageObserver?.disconnect();
    this.#hoverImageObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#hoverImageObserver?.disconnect();
        this.#hoverImageObserver = null;
        whenIdle(() => this.#warmDeferredGalleryImages());
      },
      { rootMargin: '200px' }
    );
    this.#hoverImageObserver.observe(this);
  }

  /** @type {HTMLElement | null} */
  #hoverGallery = null;

  #onGalleryPointerEnter = (/** @type {PointerEvent} */ event) => this.previewImage(event);

  #onGalleryPointerLeave = () => this.resetImage();

  /**
   * Scope the hover image swap to the gallery.
   *
   * These used to be on:pointerenter / on:pointerleave attributes on the card root, which meant
   * hovering the title, price or swatch row swapped the image too: .product-card__link is an
   * inset:0 overlay, so the pointer is "inside the card" everywhere. .card-gallery is one of the
   * few descendants with pointer-events: auto (see base.css), so it can own these events itself.
   */
  #bindGalleryHover() {
    if (this.dataset.hoverEnabled !== 'true') return;

    const gallery = this.refs.cardGallery ?? this.querySelector('.card-gallery');
    if (!(gallery instanceof HTMLElement)) return;

    this.#unbindGalleryHover();
    this.#hoverGallery = gallery;
    gallery.addEventListener('pointerenter', this.#onGalleryPointerEnter);
    gallery.addEventListener('pointerleave', this.#onGalleryPointerLeave);
  }

  #unbindGalleryHover() {
    if (!this.#hoverGallery) return;
    this.#hoverGallery.removeEventListener('pointerenter', this.#onGalleryPointerEnter);
    this.#hoverGallery.removeEventListener('pointerleave', this.#onGalleryPointerLeave);
    this.#hoverGallery = null;
  }

  /**
   * Observe the Stamped badge within this card and trigger load as soon as visible
   */
  #observeStampedBadge() {
    try {
      const badge = this.querySelector('.stamped-product-reviews-badge');
      if (!badge || !(badge instanceof HTMLElement)) return;

      if (this.#stampedObserver) {
        this.#stampedObserver.disconnect();
      }

      this.#stampedObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            refreshStampedBadges();
            this.#stampedObserver?.disconnect();
            break;
          }
        }
      }, { rootMargin: '100px' });

      this.#stampedObserver.observe(badge);
    } catch (e) {
      // no-op
    }
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /** Pointer is on the card — the shopper is about to need this image. */
  #loadDeferredGalleryImages = () => this.#loadDeferredHoverImage('auto');

  /** Speculative, ahead of any pointer — must yield to everything else. */
  #warmDeferredGalleryImages = () => this.#loadDeferredHoverImage('low');

  /**
   * @param {'auto' | 'low'} fetchPriority - Priority for the preload request.
   */
  #loadDeferredHoverImage(fetchPriority) {
    if (window.innerWidth < 750) return;

    const gallery = this.refs.cardGallery ?? this.querySelector('.card-gallery');
    if (!(gallery instanceof HTMLElement)) return;
    if (gallery.dataset.hoverImageReady === 'true' || gallery.dataset.hoverImageLoading === 'true') return;

    const deferredImages = Array.from(gallery.querySelectorAll('img[data-deferred-media="true"][data-src]'));
    if (deferredImages.length === 0) {
      gallery.dataset.hoverImageReady = 'true';
      return;
    }

    gallery.dataset.hoverImageLoading = 'true';

    const loaders = deferredImages.map((image) =>
      new Promise((resolve) => {
        if (!(image instanceof HTMLImageElement)) {
          resolve();
          return;
        }

        const nextSrc = image.dataset.src;
        const nextSrcset = image.dataset.srcset;
        const nextSizes = image.dataset.sizes;
        const nextLoading = image.dataset.loading;

        if (!nextSrc) {
          resolve();
          return;
        }

        const swap = () => {
          // The colourway can change while this preloads — a swatch click runs
          // updateGallery() in product-card-color-variants.js, which writes the
          // new src straight onto this element and clears the deferred flag.
          // Without this guard the preloader would then restore the previous
          // colour's hover image over it.
          if (image.dataset.deferredMedia !== 'true') {
            resolve();
            return;
          }
          image.src = nextSrc;
          if (nextSrcset) image.srcset = nextSrcset;
          if (nextSizes) image.sizes = nextSizes;
          if (nextLoading) image.loading = nextLoading;
          image.removeAttribute('data-src');
          image.removeAttribute('data-srcset');
          image.removeAttribute('data-sizes');
          image.removeAttribute('data-loading');
          image.removeAttribute('data-deferred-media');
          resolve();
        };

        const preloader = new Image();
        preloader.decoding = 'async';
        // Set before src, or the request is already queued at default priority.
        try {
          preloader.fetchPriority = fetchPriority;
        } catch (e) {
          // Unsupported — the request just runs at default priority.
        }
        if (nextSrcset) preloader.srcset = nextSrcset;
        if (nextSizes) preloader.sizes = nextSizes;
        preloader.src = nextSrc;

        if (preloader.decode) {
          preloader.decode().then(swap).catch(swap);
        } else {
          preloader.onload = swap;
          preloader.onerror = swap;
        }
      })
    );

    Promise.allSettled(loaders).then(() => {
      gallery.dataset.hoverImageReady = 'true';
      delete gallery.dataset.hoverImageLoading;
    });
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;
  };

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);


    // Fallback: ensure the product card link includes the selected variant id
    // Extract a variant id from common event payload shapes
    let variantId =
      event?.detail?.resource?.id ||
      event?.detail?.variant?.id ||
      event?.detail?.data?.variant?.id ||
      event?.detail?.selected?.id ||
      null;

    // Try to derive the variant id from the updated HTML if not present above
    if (!variantId && event?.detail?.data?.html) {
      try {
        const html = event.detail.data.html;
        const checkedInput = html.querySelector('input[type="radio"][data-variant-id]:checked');
        if (checkedInput?.dataset?.variantId) {
          variantId = checkedInput.dataset.variantId;
        } else {
          const selectedOpt = html.querySelector('select option:checked[data-variant-id]');
          if (selectedOpt?.dataset?.variantId) {
            variantId = selectedOpt.dataset.variantId;
          } else {
            const jsonEl = html.querySelector('script[type="application/json"]');
            if (jsonEl?.textContent) {
              const json = JSON.parse(jsonEl.textContent);
              if (json && json.id) variantId = String(json.id);
            }
          }
        }
      } catch {
        // no-op
      }
    }

    if (variantId) {
      try {
        const currentHref = this.refs.productCardLink?.href || this.productPageUrl;
        if (!currentHref) return;
        const url = new URL(currentHref, window.location.origin);
        url.searchParams.set('variant', String(variantId));
        // Keep path + query only to avoid cross-origin issues
        const href = url.pathname + url.search + url.hash;
        this.refs.productCardLink.href = href;

        // The link around each slide's media (see snippets/card-gallery.liquid) is what a click
        // on the product image follows, so it has to pick up the selected variant too.
        for (const galleryLink of this.querySelectorAll('.card-gallery__link')) {
          if (galleryLink instanceof HTMLAnchorElement) galleryLink.href = href;
        }
      } catch {
        // no-op
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    const { slideshow } = this.refs;

    // Disable hover image changes on mobile devices
    if (window.innerWidth < 750) return;

    if (!slideshow || event.pointerType !== 'mouse') return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   */
  resetImage() {
    // Disable hover image changes on mobile devices
    if (window.innerWidth < 750) return;

    const { slideshow } = this.refs;
    if (!slideshow) return;
    this.#resetVariant();
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If no explicit variant media is available, always snap back to the initial slide.
    // Using previous() can drift after dynamic media swaps (e.g., color swatch/product switches).
    if (!this.variantPicker?.selectedOption) {
      slideshow.select(0, undefined, { animate: false });
      return;
    }

    const defaultSlide = slideshow.defaultSlide;
    const slideId = defaultSlide?.getAttribute('slide-id');
    if (defaultSlide && slideshow.slides?.includes(defaultSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    const id = this.variantPicker.selectedOption.dataset.optionMediaId;
    if (!id) {
      slideshow.select(0, undefined, { animate: false });
      return;
    }

    slideshow.select({ id }, undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    const interactiveElement = event.target.closest('button, input, label, select, a, [tabindex="1"]');

    // Each slide's media is wrapped in a link so that clicking the product image is a plain link
    // click (see snippets/card-gallery.liquid). It is not the main card link, but it leads to the
    // same place, so it gets the history bookkeeping below rather than the early return — and the
    // navigation itself is left to the browser at the end of this handler.
    const galleryLink =
      interactiveElement instanceof HTMLAnchorElement && interactiveElement.classList.contains('card-gallery__link')
        ? interactiveElement
        : null;

    // If the click was on an interactive element which is not the main link, do nothing.
    if (interactiveElement && interactiveElement !== this.refs.productCardLink && !galleryLink) {
      return;
    }

    if (event?.target?.closest('[data-action="size-guide"], [data-action="find-my-size"]')) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    // An `href=""` resolves to the current page rather than reading as missing,
    // so this guard has to compare URLs, not truthiness. Without it the card
    // rewrites the address bar below and then sends the shopper to the page they
    // are already on, which reads as a broken link rather than a dead one.
    const here = new URL(window.location.href);
    if (linkURL.origin === here.origin && linkURL.pathname === here.pathname && linkURL.search === here.search) {
      return;
    }

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }
    history.replaceState({}, '', url.toString());

    // A real anchor was clicked, so the browser is already navigating (and already handles
    // modifier keys and middle-click correctly). Kicking off a second navigation here would
    // race the first, which is the failure this whole path used to be vulnerable to.
    //
    // This covers .product-card__link as well as .card-gallery__link. Both are real anchors with
    // an href, and this handler runs twice per click — once from the declarative
    // on:click="/navigateToProduct" binding on .product-card__link, once from the listener
    // connectedCallback puts on <product-card>. Scripting the navigation here therefore queued two
    // more navigations on top of the browser's own, and three racing navigations under Horizon's
    // cross-document view transitions leave the tab dead on the URL replaceState just wrote:
    // /collections/<handle>?page=N#<card anchor id>.
    if (interactiveElement instanceof HTMLAnchorElement && interactiveElement.hasAttribute('href')) return;

    this.#navigateToURL(event, linkURL);
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
